import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {catchError, Observable, of, throwError} from 'rxjs';
import {environment} from "../../../../../environments/environment";
import {
  V3Address,
  V3ContactEntry,
  V3ContactEntryDetails,
  V3Person,
  V3PersonalSensitiveDetails,
  V3PersonDetails,
  V3Settings,
} from '../../../model/v3/people';
import {V3FileItem} from "../../../model/v3/file";
import {PeopleV3Api} from "../casa-native-api";

@Injectable({
  providedIn: 'root',
})
export class PeopleV3RestService implements PeopleV3Api {
  constructor(private http: HttpClient) {
  }

  private baseUrl(path: string = ''): string {
    return `${environment.eventsApiBase}/v1/people${path}`;
  }

  getPerson(personId: number): Observable<V3Person> {
    return this.http.get<V3Person>(this.baseUrl(`/${personId}`));
  }

  updatePerson(personId: number, details: V3PersonDetails): Observable<V3Person> {
    return this.http.put<V3Person>(this.baseUrl(`/${personId}`), details);
  }

  deletePerson(personId: number): Observable<void> {
    return this.http.delete<void>(this.baseUrl(`/${personId}`));
  }

  listContacts(personId: number): Observable<V3ContactEntry[]> {
    return this.http.get<V3ContactEntry[]>(this.baseUrl(`/${personId}/contacts`));
  }

  createContact(personId: number, contact: V3ContactEntryDetails): Observable<void> {
    return this.http.post<void>(this.baseUrl(`/${personId}/contacts`), contact);
  }

  updateContact(
    personId: number,
    contactId: number,
    contact: V3ContactEntryDetails,
  ): Observable<void> {
    return this.http.put<void>(this.baseUrl(`/${personId}/contacts/${contactId}`), contact);
  }

  getAddress(personId: number): Observable<V3Address> {
    return this.http.get<V3Address>(this.baseUrl(`/${personId}/address`));
  }

  updateAddress(personId: number, address: V3Address): Observable<void> {
    return this.http.put<void>(this.baseUrl(`/${personId}/address`), address);
  }

  getSensitive(personId: number): Observable<V3PersonalSensitiveDetails> {
    return this.http.get<V3PersonalSensitiveDetails>(this.baseUrl(`/${personId}/sensitive`));
  }

  updateSensitive(
    personId: number,
    sensitive: V3PersonalSensitiveDetails,
  ): Observable<void> {
    return this.http.put<void>(this.baseUrl(`/${personId}/sensitive`), sensitive);
  }

  getPhoto(personId: number): Observable<V3FileItem> {
    return this.http
      .get<V3FileItem>(this.baseUrl(`/${personId}/photo`))
      .pipe(this.map404ToNull<V3FileItem>());
  }

  updatePhoto(personId: number, file: V3FileItem): Observable<void> {
    return this.http.put<void>(this.baseUrl(`/${personId}/photo`), file);
  }

  getSettings(personId: number): Observable<V3Settings> {
    return this.http.get<V3Settings>(this.baseUrl(`/${personId}/settings`));
  }

  updateSettings(personId: number, settings: V3Settings): Observable<void> {
    return this.http.put<void>(this.baseUrl(`/${personId}/settings`), settings);
  }

  private map404ToNull<T>(): (source: Observable<T>) => Observable<T> {
    return (source: Observable<T>) =>
      source.pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(null as T);
          }
          return throwError(() => error);
        })
      );
  }
}
